import Grid from "@mui/material/Grid2";
import { Box, FormControl, Typography } from "@mui/material";
import { isEmpty } from "lodash";
import { useEffect, useRef } from "react";
import { Control, UseFormClearErrors, useWatch } from "react-hook-form";

import {
  displayFlex,
  flexColumn,
  flexRow,
} from "../../../styles/mui/styles/display";
import { FormInputText } from "../../../components/FormPriorAuthReAuth/FormInputText";
import { rules } from "../../../utils/validation/Validation";
import { ListOptions } from "../../../models/Master";
import { MyAccountModel } from "../../../models/MyAccount";
import { ServiceProvider } from "../../../models/ServiceProvider";
import { phoneFaxFormat } from "../../../constants/Regex";
import { getPhoneNumber, getValue, getZipCode } from "../../../utils";
import { Numbers } from "../../../constants/Referral";
import { AgencyListPayload } from "../../../models/ProviderMaterials";
import ReadOnlyField from "../../../components/FormPriorAuthReAuth/ReadOnlyField";
import fontWeight from "../../../styles/mui/fontWeight";
import colors from "../../../styles/colors/colors";
import { FormInputDropdown } from "../../../components/FormPriorAuthReAuth/FormInputDropdown";
import { FormSelectAutoComplete } from "../../../components/AuthFormComponents/FormSelectAutoComplete";
import { TabSessionStorageResponse } from "../../../models/api/TabSessionStorage";
import { TabData } from "../../../models/TabSessionStorage";
import { FieldSpacing, agencyFieldWidth } from "../styles/style";
import { PhoneFaxExtInput } from "../../../components/FormPriorAuthReAuth/PhoneFaxExtInput";
import { BenefitPlanDetails } from "../../../models/Patient";
import { updateTabStatus } from "../../../constants/TabSessionStorage";

export interface PropsFromState {
  masterServiceProviderState: ListOptions[];
  masterSendCommunicationAuthTypeState: ListOptions[];
  loggedInUser: MyAccountModel;
  serviceProviderListWithDetails: ServiceProvider[];
  TabSessionStorage: TabSessionStorageResponse;
  benefitPlanUidState: BenefitPlanDetails;
  control: Control<any>;
  setValue: (id: any, value: any, boolean?: any) => void;
  trigger: any;
  getValues: any;
  reset: any;
  clearErrors: UseFormClearErrors<TabData>;
}

export interface PropsFromDispatch {
  getMasterServiceProvider: (payload: AgencyListPayload) => void;
  getSendCommunicationAuthType: () => void;
  saveTabSessionStorage: (sessionData: TabData) => void;
  setReimbursementTypeId: (id: number) => void;
}

type AllProps = PropsFromState & PropsFromDispatch;

const AgencyDetails: React.FC<AllProps> = ({
  serviceProviderListWithDetails,
  getMasterServiceProvider,
  masterServiceProviderState,
  getSendCommunicationAuthType,
  masterSendCommunicationAuthTypeState,
  loggedInUser,
  benefitPlanUidState,
  control,
  setValue,
  trigger,
  getValues,
  setReimbursementTypeId,
  reset,
  clearErrors
}) => {
  const prevApprovedAuthPreference = useRef<number | null>(null);
  const isInitialLoad = useRef(true);
  
  useEffect(() => {
    getSendCommunicationAuthType();
    if (
      !isEmpty(benefitPlanUidState) &&
      getValue(benefitPlanUidState, "benefitPlanUid")?.length > Numbers.ZERO
    ) {
      getMasterServiceProvider({
        healthPlanId: benefitPlanUidState.healthPlanId,
        benefitPlanId: benefitPlanUidState.benefitPlanId,
        email: loggedInUser.providerPortalUser.email,
      });
    } else {
      getMasterServiceProvider({
        healthPlanId: null,
        benefitPlanId: null,
        email: loggedInUser.providerPortalUser.email,
      });
    }
    setValue(
      "agencyDetails.requesterName",
      loggedInUser.providerPortalUser.userName
    );
    setValue(
      "agencyDetails.requesterEmail",
      loggedInUser.providerPortalUser.email
    );
  }, [benefitPlanUidState]);

  const getAgencyPhoneAndFax = (value: number) => {
    const listObject =
      serviceProviderListWithDetails &&
      serviceProviderListWithDetails.filter(
        (item: ServiceProvider) => value === item.serviceProviderId
      );
    return listObject[0];
  };

  const [streetName1, streetName2, city, requesterState, zipCode] = useWatch({
    control,
    name: [
      "agencyDetails.streetName1",
      "agencyDetails.streetName2",
      "agencyDetails.city",
      "agencyDetails.requesterState",
      "agencyDetails.zipCode",
    ],
  });

  const onChangeHandler = (
    _e: React.ChangeEvent<HTMLInputElement>,
    value: number,
    name: string
  ) => {
    if (value === undefined && name === "agencyName") {
      setValue("agencyDetails.requesterPhoneNo", "");
      setValue("agencyDetails.requesterFax", "");
      return;
    }

    const provider = getAgencyPhoneAndFax(value);

    if (!isEmpty(provider) && name === "agencyName") {
      setValue(
        "agencyDetails.requesterPhoneNo",
        phoneFaxFormat.test(provider.phone)
          ? provider.phone
          : getPhoneNumber(provider.phone)
      );
      setValue(
        "agencyDetails.requesterFax",
        phoneFaxFormat.test(provider.fax)
          ? provider.fax
          : getPhoneNumber(provider.fax)
      );
      trigger(["agencyDetails.requesterPhoneNo", "agencyDetails.requesterFax"]);
      reset({
        ...getValues(),
        agencyDetails: {
          ...getValues("agencyDetails"),
          requesterNpi: provider.providerNpi,
          facilityName: provider.providerName,
          serviceProviderId: provider.serviceProviderId,
          streetName1: provider.streetName1,
          streetName2: provider.streetName2,
          county: null,
          city: provider.providerCity,
          zipCode: getZipCode(provider.zipCode),
          requesterState: provider.providerState,
          facilityTypeId: "1",
          requesterPhoneNo: phoneFaxFormat.test(provider.phone)
            ? provider.phone
            : getPhoneNumber(provider.phone),
          requesterFax: phoneFaxFormat.test(provider.fax)
            ? provider.fax
            : getPhoneNumber(provider.fax),
          requesterName: loggedInUser.providerPortalUser.userName,
          requesterEmail: loggedInUser.providerPortalUser.email,
        },
        referralServiceProvider: {
          ...getValues("referralServiceProvider"),
          userCreated: loggedInUser?.providerPortalUser?.email,
          fullNameCreated: `${loggedInUser?.providerPortalUser?.firstName} ${loggedInUser?.providerPortalUser?.lastName}`,
          referralServiceProviderId: 0,
          serviceProviderId: provider.serviceProviderId,
          providerName: provider.providerName,
          providerAddress: provider.providerAddress,
          providerNpi: provider.providerNpi,
          streetName1: provider.streetName1,
          streetName2: provider.streetName2,
          providerCity: provider.providerCity,
          providerState: provider.providerState,
          zipCode: getZipCode(provider.zipCode),
          phone: getPhoneNumber(provider.phone),
          reimbursementTypeName: provider.reimbursementTypeName,
          reimbursementTypeId: provider.reimbursementTypeId,
          extension: provider?.extension ?? "",
          providerId: provider?.providerId ?? 0,
          plexisOfficeId: provider?.plexisOfficeId ?? 0,
        },
      });

      setReimbursementTypeId(provider.reimbursementTypeId);

    }
  };

  useEffect(() => {
      reset({
        ...getValues(),
        tabStatus: updateTabStatus(0, getValues("tabStatus")),
      });
    }, []);

  const approvedAuthPreference = useWatch({
    control,
    name: "agencyDetails.sendApprovedAuthTo",
  });

  useEffect(() => {
    if (isInitialLoad.current) {
      isInitialLoad.current = false;
    } else if (
      prevApprovedAuthPreference.current !== null &&
      prevApprovedAuthPreference.current !== approvedAuthPreference
    ) {
      setValue("agencyDetails.secondaryEmailAddress", "");
      clearErrors("agencyDetails.secondaryEmailAddress");
    }
    prevApprovedAuthPreference.current = approvedAuthPreference;
  }, [approvedAuthPreference]);

  return (
    <>
      <Grid size={12}>
        <Grid container>
          <Grid size={12} sx={flexColumn}>
            <Grid container spacing={2} sx={flexRow}>
              <Grid size={{ xs: 12 }} sx={{ paddingX: 2 }}>
                <Box sx={displayFlex}>
                  <Typography
                    variant="body1"
                    fontWeight={fontWeight[2]}
                    color={colors.fonts[2]}
                  >
                    Accepting Agency Name/Branch:
                  </Typography>
                  <Typography
                    variant="subtitle2"
                    fontWeight={fontWeight[2]}
                    color={colors.red[103]}
                    pl={"0.3rem"}
                  >
                    *
                  </Typography>
                </Box>
                <Box sx={displayFlex}>
                  <Typography
                    variant="body2"
                    fontWeight={fontWeight[2]}
                    color={colors.fonts[2]}
                    style={{ fontStyle: "italic" }}
                  >
                    *Please select an agency/location from the options listed within the dropdown
                  </Typography>
                </Box>
                <FormControl variant="standard" sx={agencyFieldWidth}>
                  <FormSelectAutoComplete
                    name={"agencyDetails.serviceProviderId"}
                    control={control}
                    onChangeHandler={(
                      e: React.ChangeEvent<HTMLInputElement>,
                      value: number
                    ) => {
                      onChangeHandler(e, value, "agencyName");
                    }}
                    list={masterServiceProviderState}
                    isMultiLine={true}
                    required={true}
                    placeholder={"Select an Agency"}
                  />
                </FormControl>
              </Grid>
            </Grid>
            <Grid container spacing={2} sx={flexRow} mt={2}>
              <Grid size={{ xs: 4.5 }} sx={FieldSpacing}>
                <ReadOnlyField
                  label={"Agency NPI:"}
                  value={getValues("agencyDetails.requesterNpi")}
                  mandatorySymbol={true}
                />
              </Grid>
              <Grid size={{ xs: 4 }} sx={FieldSpacing}>
                <FormInputText
                  control={control}
                  helper={rules.agencyContactName}
                  name={"agencyDetails.requesterName"}
                  label="Agency Contact Name: "
                  textLength={128}
                  disabled={false}
                />
              </Grid>
            </Grid>
            <Grid container spacing={2} sx={flexRow} mt={2}>
              <Grid size={{ xs: 4.5 }} sx={FieldSpacing}>
                <ReadOnlyField label={"Agency Street 1:"} value={streetName1} mandatorySymbol={true} />
              </Grid>
              <Grid size={{ xs: 4 }} sx={FieldSpacing}>
                <FormInputText
                  control={control}
                  helper={rules.agencyEmail}
                  name={"agencyDetails.requesterEmail"}
                  label="Agency Email Address:"
                  textLength={128}
                  disabled={false}
                />
              </Grid>
            </Grid>
            <Grid container spacing={2} sx={flexRow} mt={2}>
              <Grid size={{ xs: 4.5 }} sx={FieldSpacing}>
                <ReadOnlyField label={"Agency Street 2:"} value={streetName2?.trim() ? streetName2.trim() : "-"} />
              </Grid>
              <Grid size={{ xs: 4 }} sx={FieldSpacing}>
                <PhoneFaxExtInput
                  label="Agency Phone:"
                  phoneFaxName="agencyDetails.requesterPhoneNo"
                  control={control}
                  rules={rules.agencyPhoneNumber}
                  showIcon={false}
                />
              </Grid>
            </Grid>
            <Grid container spacing={2} sx={flexRow} mt={2}>
              <Grid size={{ xs: 4.5 }} sx={FieldSpacing}>
                <ReadOnlyField label={"Agency City:"} value={city} mandatorySymbol={true} />
              </Grid>
              <Grid size={{ xs: 4 }} sx={FieldSpacing}>
                <PhoneFaxExtInput
                  label="Agency Fax:"
                  phoneFaxName="agencyDetails.requesterFax"
                  control={control}
                  rules={rules.agencyFaxNumber}
                  showIcon={false}
                />
              </Grid>
            </Grid>
            <Grid container spacing={2} sx={flexRow}>
              <Grid size={{ xs: 4.5 }} sx={FieldSpacing}>
                <ReadOnlyField label={"Agency State:"} value={requesterState} mandatorySymbol={true} />
              </Grid>
              <Grid size={{ xs: 4 }} sx={FieldSpacing}>
                <Box sx={displayFlex}>
                  <Typography
                    variant="body1"
                    fontWeight={fontWeight[2]}
                    color={colors.fonts[2]}
                  >
                    Send Approved Auth to:
                  </Typography>
                  <Typography
                    variant="subtitle2"
                    fontWeight={fontWeight[2]}
                    color={colors.red[103]}
                    pl={"0.3rem"}
                  >
                    *
                  </Typography>
                </Box>
                <Box sx={displayFlex}>
                  <Typography
                    variant="body2"
                    fontWeight={fontWeight[2]}
                    color={colors.fonts[2]}
                    style={{ fontStyle: "italic" }}
                  >
                    *Documentation including PHI may be sent to the choice below
                  </Typography>
                </Box>
                <Box pt={0.8}>
                  <FormControl variant="standard" fullWidth>
                    <FormInputDropdown
                      control={control}
                      name={`agencyDetails.sendApprovedAuthTo`}
                      list={masterSendCommunicationAuthTypeState}
                      arrowDropDownIcon={false}
                      helper={rules.sendApprovedAuthTo}
                      mandatorySymbol={true}
                      onChangeHandler={(
                        e: React.ChangeEvent<HTMLInputElement>,
                        value: number
                      ) => {
                        onChangeHandler(e, value, "sendApprovedAuthTo");
                      }}
                    />
                  </FormControl>
                </Box>
              </Grid>
            </Grid>
            <Grid container spacing={2} sx={flexRow}>
              <Grid size={{ xs: 4.5 }} sx={FieldSpacing}>
                <ReadOnlyField label={"Agency Zip:"} value={zipCode} mandatorySymbol={true} />
              </Grid>
              {approvedAuthPreference === Numbers.ONE && (
                <Grid size={{ xs: 4 }} sx={FieldSpacing}>
                  <Box sx={displayFlex}>
                    <Typography
                      variant="body1"
                      fontWeight={fontWeight[2]}
                      color={colors.fonts[2]}
                    >
                      Notification of Decisions:
                    </Typography>
                  </Box>
                  <Box sx={displayFlex}>
                    <Typography
                      variant="body2"
                      fontWeight={fontWeight[2]}
                      color={colors.fonts[2]}
                      style={{ fontStyle: "italic" }}
                    >
                      *An email will be sent when your request has a decision. A secondary email address may be provided below for this notification.
                    </Typography>
                  </Box>
                  <Box sx={displayFlex} pt={0.8}>
                    <FormInputText
                      control={control}
                      helper={rules.emailPattern}
                      name={"agencyDetails.secondaryEmailAddress"}
                      textLength={64}
                      disabled={false}
                      label="Secondary Address"
                    />
                  </Box>
                </Grid>
              )}
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </>
  );
};

export default AgencyDetails;
